import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowDown } from "lucide-react";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { delay: 0.4 + i * 0.12, duration: 1.1, ease: [0.19, 1, 0.22, 1] },
  }),
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);
  const overlay = useTransform(scrollYProgress, [0, 1], [0.12, 0.42]);

  return (
    <section ref={ref} id="top" className="relative h-[100vh] min-h-[720px] w-full overflow-hidden bg-[#1A1A1A]" data-testid="hero-section">
      {/* Parallax image */}
      <motion.div style={{ y, scale }} className="absolute inset-0 will-change-transform">
        <img
          alt="Aerial view of a tropical Catalonia resort with palapas, pools and Caribbean beach"
          src="/hero-resort.jpg"
          className="w-full h-full object-cover ken-burns"
        />
        <motion.div style={{ opacity: overlay }} className="absolute inset-0 bg-black" />
        {/* Bottom vignette only — keep tropical colors bright */}
        <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-[#1A1A1A]/85 via-[#1A1A1A]/35 to-transparent" />
      </motion.div>

      {/* Nav-safe hero content */}
      <div className="relative z-10 h-full max-w-[1600px] mx-auto px-6 md:px-12 flex flex-col justify-end pb-20 md:pb-28">
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3, duration: 0.8 }}
          className="flex items-center gap-4 mb-8"
        >
          <span className="h-px w-10 bg-[#C5A059]" />
        </motion.div>

        <h1 className="text-[#F9F6F0] font-serif-editorial leading-[0.92] tracking-tight max-w-[1400px]"
            style={{ fontSize: "clamp(3.2rem, 9.2vw, 10rem)" }}>
          <span className="mask-line"><motion.span variants={line} initial="hidden" animate="show" custom={0} className="block">VIP status,</motion.span></span>
          <span className="mask-line"><motion.span variants={line} initial="hidden" animate="show" custom={1} className="block italic text-[#E6C5C0]">without the</motion.span></span>
          <span className="mask-line"><motion.span variants={line} initial="hidden" animate="show" custom={2} className="block">memberships.</motion.span></span>
        </h1>

        <div className="mt-10 md:mt-14 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <motion.p
            initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 1.4, duration: 0.8 }}
            className="text-[#F9F6F0]/85 max-w-lg text-base md:text-lg leading-relaxed"
          >
            Luxury resort stays with the privileges typically reserved for VIP members &mdash; personally curated for you across select resorts in Mexico and the Dominican Republic. No membership. No complicated booking portal. Just an elevated escape designed around you.
          </motion.p>
          <motion.div
            initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 1.55, duration: 0.8 }}
            className="flex flex-col items-start gap-3"
          >
            <div className="flex items-center gap-4">
              <a href="#inquiry" data-testid="hero-cta-inquire" className="ee-btn-primary" style={{ background:"#F9F6F0", color:"#1A1A1A", borderColor:"#F9F6F0" }}>
                Begin My Inquiry
              </a>
              <a href="#perks-showcase" data-testid="hero-cta-included" className="ee-btn-ghost" style={{ color:"#F9F6F0", borderColor:"rgba(249,246,240,0.6)" }}>
                See What&rsquo;s Included
              </a>
            </div>
            <div data-testid="hero-book-now-pay-later">
              <p className="eyebrow text-[#C5A059] mb-1">Book Now · Pay Later</p>
              <p className="text-sm text-[#F9F6F0]/75 leading-relaxed">Flexible payment options to fit your lifestyle.</p>
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2, duration: 1 }}
          className="absolute bottom-8 right-6 md:right-12 flex items-center gap-3 text-[#F9F6F0]/70"
        >
          <span className="eyebrow">Scroll</span>
          <ArrowDown size={14} strokeWidth={1.25} />
        </motion.div>
      </div>
    </section>
  );
}
